import MODEL from './xgGeometryCandidateModel.json';

// Offline-trained, geometry-only candidate. It is intentionally an explicit
// API: existing xG callers remain on the established model until the capture
// and outcome promotion gates are satisfied.
export const XG_GEOMETRY_CANDIDATE_VERSION = MODEL.schemaVersion;
export const XG_GEOMETRY_CANDIDATE_MODEL = MODEL;

const finite = value => {
  if (value === null || value === undefined || typeof value === 'boolean') return null;
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  if (typeof value === 'string' && value.trim() === '') return null;
  return Number.isFinite(Number(value)) ? Number(value) : null;
};
const teamName = value => {
  const text = String(value || '').trim().toLowerCase();
  return text === 'red' || text === 'r' ? 'red' : text === 'blue' || text === 'b' ? 'blue' : null;
};
const point = value => {
  const x = finite(Array.isArray(value) ? value[0] : value?.x);
  const y = finite(Array.isArray(value) ? value[1] : value?.y);
  return x === null || y === null ? null : { x, y };
};

const resolveGoal = (shot, options) => {
  const goalX = finite(shot?.goalX ?? shot?.goal_x ?? shot?.targetGoalX ?? shot?.target_goal_x);
  const target = teamName(shot?.targetGoal ?? shot?.targetTeam ?? shot?.target_team)
    || (teamName(shot?.team) === 'red' ? 'blue' : teamName(shot?.team) === 'blue' ? 'red' : null);
  const goals = Array.isArray(options?.stadium?.goals)
    ? options.stadium.goals
    : Array.isArray(options?.geometry?.goals) ? options.geometry.goals : [];
  const candidates = goals.map(raw => {
    const p0 = point(raw?.p0); const p1 = point(raw?.p1);
    if (!p0 || !p1 || (target && teamName(raw?.team) !== target)) return null;
    const dx = p1.x - p0.x; const dy = p1.y - p0.y; const length = Math.hypot(dx, dy);
    if (!(length > 1e-6)) return null;
    return { raw, x: (p0.x + p1.x) / 2, center: { x: (p0.x + p1.x) / 2, y: (p0.y + p1.y) / 2 }, tangent: { x: dx / length, y: dy / length }, normal: { x: -dy / length, y: dx / length }, halfWidth: length / 2 };
  }).filter(Boolean);
  if (candidates.length) {
    candidates.sort((a, b) => Math.abs(a.x - (goalX ?? a.x)) - Math.abs(b.x - (goalX ?? b.x)));
    return { ...candidates[0], provenance: 'stadium-goal', candidateCount: candidates.length };
  }
  if (goalX !== null) return { center: { x: goalX, y: 0 }, tangent: { x: 0, y: 1 }, normal: { x: 1, y: 0 }, halfWidth: 130, provenance: 'legacy-goalX-center0-width130', candidateCount: 0 };
  return null;
};

const featuresFor = (shot, options = {}) => {
  const from = point(shot?.from); const goal = resolveGoal(shot, options);
  if (!from || !goal) return null;
  const dx = from.x - goal.center.x; const dy = from.y - goal.center.y;
  const depth = Math.abs(dx * goal.normal.x + dy * goal.normal.y);
  const lateral = Math.abs(dx * goal.tangent.x + dy * goal.tangent.y);
  const distance = Math.hypot(depth, lateral); const halfWidth = Math.max(1, goal.halfWidth);
  const lateralNorm = lateral / halfWidth; const safeDepth = Math.max(1, depth);
  const angle = Math.atan2(lateral, safeDepth);
  return { values: [Math.log1p(safeDepth), Math.log1p(Math.max(1, distance)), lateralNorm, Math.max(0, 1 - lateralNorm), angle, angle * angle, 1 / (1 + safeDepth), safeDepth / halfWidth], provenance: goal.provenance, candidateCount: goal.candidateCount };
};

const sigmoid = value => value >= 0 ? 1 / (1 + Math.exp(-value)) : Math.exp(value) / (1 + Math.exp(value));

/** Return null when the pre-kick state lacks finite geometry. */
export function computeGeometryCandidateXg(shot, options = {}) {
  const extracted = featuresFor(shot, options);
  if (!extracted || extracted.values.length !== MODEL.weights.length - 1) return null;
  let logit = MODEL.weights[0];
  extracted.values.forEach((value, index) => { logit += MODEL.weights[index + 1] * ((value - MODEL.means[index]) / MODEL.scales[index]); });
  return { xg: sigmoid(logit), modelVersion: XG_GEOMETRY_CANDIDATE_VERSION, provenance: extracted.provenance, candidateGoalSegments: extracted.candidateCount, features: extracted.values };
}

export default computeGeometryCandidateXg;
